import { Duration, addDays, formatDuration, formatISO } from 'date-fns';
import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';

import { useCurrencies } from '@/bits';
import {
  Button,
  Card,
  CardCloseButton,
  CardOptions,
  DateTimeField,
  ErrorMessage,
  GameSearch,
  NakedForm,
  NumberField,
  SelectField,
  SelectOption,
  TextField,
  TextareaField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import {
  ClaimType,
  GameProvider,
  RewardTypeEnum,
  Scalars,
} from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import { convertValuesToNumbers, rewardGameProviders } from '@/utils';
import { assert } from '@/utils/error';
import {
  CreatePlayerBonusMoneyDropMutation,
  CreatePlayerBonusMoneyDropMutationVariables,
  CreatePlayerFreeSpinsMutation,
  CreatePlayerFreeSpinsMutationVariables,
  CreatePlayerMoneyDropMutation,
  CreatePlayerMoneyDropMutationVariables,
  PlayerRewardCreateFormQuery,
} from './__generated__/PlayerRewardCreateForm';
import AutoCalculateRewardButton from './AutoCalculateRewardButton';
import { usePlayerRewardCalculator } from './usePlayerRewardCalculator';

const query = graphql`
  query SanityPlayerRewardCreateForm {
    sanityPlayerRewardCreateForm {
      title {
        ...SanityLocaleString
      }
      rewardCreated {
        ...SanityLocaleString
      }
      rewardType {
        ...SanityLocaleString
      }
      value {
        ...SanityLocaleString
      }
      currency {
        ...SanityLocaleString
      }
      numberOfSpins {
        ...SanityLocaleString
      }
      paylines {
        ...SanityLocaleString
      }
      game {
        ...SanityLocaleString
      }
      gameProviders {
        ...SanityLocaleString
      }
      nameOfReward {
        ...SanityLocaleString
      }
      startDate {
        ...SanityLocaleString
      }
      endDate {
        ...SanityLocaleString
      }
      claimableInstant {
        ...SanityLocaleString
      }
      notes {
        ...SanityLocaleString
      }
      customReward {
        ...SanityLocaleString
      }
      expiresIn {
        ...SanityLocaleString
      }
      maxPayout {
        ...SanityLocaleString
      }
      wageringRequirementFactor {
        ...SanityLocaleString
      }
      autoCalculateReward {
        ...SanityLocaleString
      }
    }
  }
`;

const createFreeSpinsMutation = gql`
  mutation CreatePlayerFreeSpins(
    $rewardName: String!
    $claimType: ClaimType!
    $startsAt: OffsetDateTime!
    $endsAt: OffsetDateTime
    $expiresIn: Duration
    $comment: String!
    $playerIds: [String!]!
    $spinsNumber: PositiveInt!
    $spinsValue: PositiveLong!
    $betLevel: PositiveInt!
    $currencyCode: ISOCurrencyCode!
    $gameProviderId: GameProvider!
    $gameMobileId: String
    $gameDesktopId: String
  ) {
    createFreeSpins: createFreeSpinsV2(
      rewardName: $rewardName
      claimType: $claimType
      startsAt: $startsAt
      endsAt: $endsAt
      expiresIn: $expiresIn
      comment: $comment
      playerIds: $playerIds
      spinsNumber: $spinsNumber
      spinsValue: $spinsValue
      betLevel: $betLevel
      currencyCode: $currencyCode
      gameProviderId: $gameProviderId
      gameMobileId: $gameMobileId
      gameDesktopId: $gameDesktopId
    )
  }
`;

const createMoneyDropMutation = gql`
  mutation CreatePlayerMoneyDrop(
    $rewardName: String!
    $claimType: ClaimType!
    $startsAt: OffsetDateTime!
    $endsAt: OffsetDateTime
    $expiresIn: Duration
    $comment: String!
    $playerIds: [String!]!
    $amount: PositiveBigDecimal!
    $currencyCode: ISOCurrencyCode!
  ) {
    createMoneyDrop(
      rewardName: $rewardName
      claimType: $claimType
      startsAt: $startsAt
      endsAt: $endsAt
      expiresIn: $expiresIn
      comment: $comment
      playerIds: $playerIds
      amount: $amount
      currencyCode: $currencyCode
    )
  }
`;

const createBonusMoneyDropMutation = gql`
  mutation CreatePlayerBonusMoneyDrop(
    $rewardName: String!
    $claimType: ClaimType!
    $startsAt: OffsetDateTime!
    $endsAt: OffsetDateTime
    $expiresIn: Duration
    $comment: String!
    $playerIds: [String!]!
    $amount: PositiveBigDecimal!
    $currencyCode: ISOCurrencyCode!
    $maxPayout: PositiveBigDecimal!
    $wageringRequirementFactor: PositiveInt!
  ) {
    createBonusMoneyDrop: createBonusMoneyDropV2(
      rewardName: $rewardName
      claimType: $claimType
      startsAt: $startsAt
      endsAt: $endsAt
      expiresIn: $expiresIn
      comment: $comment
      playerIds: $playerIds
      amount: $amount
      currencyCode: $currencyCode
      maxPayout: $maxPayout
      wageringRequirementFactor: $wageringRequirementFactor
    )
  }
`;

const playerQuery = gql`
  query PlayerRewardCreateForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      uuid
      firstName
      lastName
    }
  }
`;

type Rewards = 'FreeSpinsType' | 'MoneyDropType' | 'BonusMoneyDropType';

type RewardOption = SelectOption & { value: Rewards };

type FormValues = {
  rewardName: string;
  rewardType: RewardTypeEnum;
  claimType: ClaimType;
  startsAt: Date;
  endsAt?: Date | null;
  expiresIn?: Duration | null;
  comment: string;
  spinsNumber: number | null;
  spinsValue: number | null;
  amount: number | null;
  bonusAmount: number | null;
  betLevel: number;
  gameProvider: GameProvider;
  currencyCode: Scalars['ISOCurrencyCode']['input'];
  gameDetails: {
    mobileId?: string | null;
    desktopId?: string | null;
  };
  maxPayout?: number | null;
  wageringRequirementFactor?: number | null;
};

const useOptions = () => {
  return useMemo(() => {
    const rewardTypesOptions: RewardOption[] = [
      {
        label: 'FreeSpins',
        value: RewardTypeEnum.FreeSpinsType,
      },
      {
        label: 'MoneyDrop',
        value: RewardTypeEnum.MoneyDropType,
      },
      {
        label: 'BonusMoneyDrop',
        value: RewardTypeEnum.BonusMoneyDropType,
      },
    ];

    const claimOptions: SelectOption[] = [
      {
        label: 'Claimable',
        value: ClaimType.Manual,
      },
      {
        label: 'Instant',
        value: ClaimType.Instant,
      },
    ];

    return {
      rewardTypesOptions,
      claimOptions,
    };
  }, []);
};

const PlayerRewardCreateForm: FC<{ playerId: string }> = ({ playerId }) => {
  const { t } = useTranslate();
  const staticData =
    useStaticQuery<Queries.SanityPlayerRewardCreateFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const { close } = useModalContext();

  const [autoCalculateRewardUsed, setAutoCalculateRewardUsed] = useState<
    Partial<Record<RewardTypeEnum, boolean>>
  >({
    [RewardTypeEnum.FreeSpinsType]: false,
    [RewardTypeEnum.BonusMoneyDropType]: false,
  });

  const form = staticData.sanityPlayerRewardCreateForm;
  assert(form, 'missing form data');

  const {
    data: {
      rewardAmount,
      spinsValue,
      nrOfSpins,
      maxWin,
      wagering,
      playerRewardStats,
    },
    fetching,
  } = usePlayerRewardCalculator(playerId);

  const { rewardTypesOptions, claimOptions } = useOptions();
  const { currencyOptions } = useCurrencies();

  const currencyCode = playerRewardStats?.currency || 'EUR';

  const [{ data }] = useQuery<PlayerRewardCreateFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  const [createFreeSpinsState, createFreeSpins] = useMutation<
    CreatePlayerFreeSpinsMutation,
    CreatePlayerFreeSpinsMutationVariables
  >(createFreeSpinsMutation);

  const [createMoneyDropState, createMoneyDrop] = useMutation<
    CreatePlayerMoneyDropMutation,
    CreatePlayerMoneyDropMutationVariables
  >(createMoneyDropMutation);

  const [createBonusMoneyDropState, createBonusMoneyDrop] = useMutation<
    CreatePlayerBonusMoneyDropMutation,
    CreatePlayerBonusMoneyDropMutationVariables
  >(createBonusMoneyDropMutation);

  const defaultValues: FormValues = {
    rewardType: RewardTypeEnum.FreeSpinsType,
    claimType: ClaimType.Manual,
    rewardName: `${t(form.customReward)} ${data?.player.firstName} ${
      data?.player.lastName
    }`,
    startsAt: new Date(),
    endsAt: addDays(new Date(), 14),
    expiresIn: { days: 3 },
    comment: '',
    spinsNumber: null,
    spinsValue: spinsValue ?? null,
    amount: null,
    bonusAmount: null,
    betLevel: 1,
    gameProvider: GameProvider.Relax,
    gameDetails: {
      mobileId: '',
      desktopId: '',
    },
    currencyCode,
    maxPayout: 1000,
    wageringRequirementFactor: 40,
  };

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const { watch, setValue, reset, getValues } = methods;
  const rewardType = watch('rewardType');
  const selectedGameProvider = watch('gameProvider');
  const isFreeSpins = rewardType === 'FreeSpinsType';
  const isBonusMoneyDrop = rewardType === 'BonusMoneyDropType';
  const isMoneyDrop = rewardType === 'MoneyDropType';

  useEffect(() => {
    if (!fetching && playerRewardStats?.currency) {
      setValue('currencyCode', playerRewardStats.currency);
    }
  }, [fetching, playerRewardStats?.currency, setValue]);

  const isAutoCalculateRewardVisible = isFreeSpins || isBonusMoneyDrop;

  const hasIneligibilityReasons =
    (playerRewardStats?.ineligibilityReasons?.length ?? 0) > 0;

  const isAutoCalculateRewardDisabled =
    !playerRewardStats?.isEligibleForReward ||
    hasIneligibilityReasons ||
    autoCalculateRewardUsed[rewardType];

  const autoGeneratedRewardName = `Auto Calculated Reward: ${playerRewardStats?.playerCurrentSegment} - ${playerRewardStats?.ngr}`;

  const onCalculateReward = useCallback(() => {
    setValue('rewardName', autoGeneratedRewardName);

    if (isFreeSpins && nrOfSpins != null && spinsValue != null) {
      setValue('spinsNumber', nrOfSpins);
      setValue('spinsValue', spinsValue);
    }

    if (isBonusMoneyDrop && rewardAmount != null) {
      setValue('bonusAmount', rewardAmount);

      if (maxWin != null) {
        setValue('maxPayout', maxWin);
      }

      if (wagering != null) {
        setValue('wageringRequirementFactor', wagering);
      }
    }

    setAutoCalculateRewardUsed((prev) => ({
      ...prev,
      [rewardType]: true,
    }));
  }, [
    autoGeneratedRewardName,
    isFreeSpins,
    nrOfSpins,
    spinsValue,
    isBonusMoneyDrop,
    rewardAmount,
    maxWin,
    wagering,
    rewardType,
    setValue,
  ]);

  const onSubmit = (values: FormValues) => {
    const commonVariables = {
      ...values,
      startsAt: formatISO(values.startsAt),
      endsAt: values.endsAt ? formatISO(values.endsAt) : null,
      playerIds: data?.player.uuid ? [data?.player.uuid] : [''],
      expiresIn: values.expiresIn
        ? formatDuration(convertValuesToNumbers(values.expiresIn))
        : null,
    };

    if (values.rewardType === 'FreeSpinsType') {
      const variables: CreatePlayerFreeSpinsMutationVariables = {
        ...commonVariables,
        spinsNumber: Number(values.spinsNumber),
        spinsValue: Number(values.spinsValue),
        betLevel: Number(values.betLevel),
        gameProviderId: selectedGameProvider,
        gameMobileId: values.gameDetails.mobileId,
        gameDesktopId: values.gameDetails.desktopId,
      };

      return createFreeSpins(variables).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error.message);
        } else if (close) {
          feedback.success(t(form.rewardCreated));
          close();
        }
      });
    }

    if (values.rewardType === 'BonusMoneyDropType') {
      const variables: CreatePlayerBonusMoneyDropMutationVariables = {
        ...commonVariables,
        amount: Number(values.bonusAmount),
        maxPayout: Number(values.maxPayout),
        wageringRequirementFactor: Number(values.wageringRequirementFactor),
      };

      return createBonusMoneyDrop(variables).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error.message);
        } else if (close) {
          feedback.success(t(form.rewardCreated));
          close();
        }
      });
    }

    const variables: CreatePlayerMoneyDropMutationVariables = {
      ...commonVariables,
      amount: Number(values.amount),
    };

    return createMoneyDrop(variables).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        feedback.success(t(form.rewardCreated));
        close();
      }
    });
  };

  const resetAllButRewardType = useCallback(() => {
    const currentValues = getValues();
    reset();
    setValue('rewardType', currentValues.rewardType);

    setAutoCalculateRewardUsed((prev) => ({
      ...prev,
      [rewardType]: false,
    }));
  }, [getValues, reset, setValue, rewardType]);

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex p-6">
        <NakedForm className="w-full" onSubmit={onSubmit} methods={methods}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div className="grid gap-2 content-start">
              <SelectField
                name="rewardType"
                id="RewardPlayerCreateForm__rewardType"
                title={t(form.rewardType)}
                required
                options={rewardTypesOptions}
              />
              {isAutoCalculateRewardVisible && (
                <AutoCalculateRewardButton
                  disabled={isAutoCalculateRewardDisabled}
                  onClick={onCalculateReward}
                  rewardDetails={playerRewardStats}
                  fetching={fetching}
                />
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                {isFreeSpins && (
                  <div className="flex">
                    <TextField
                      name="spinsValue"
                      required
                      id="RewardPlayerCreateForm__spinsValue"
                      title={t(form.value) + ' (cents)'}
                      prefix="€"
                    />
                  </div>
                )}
                {isMoneyDrop && (
                  <TextField
                    name="amount"
                    required
                    id="RewardPlayerCreateForm__amount"
                    title={t(form.value)}
                  />
                )}
                {isBonusMoneyDrop && (
                  <TextField
                    name="bonusAmount"
                    required
                    id="RewardPlayerCreateForm__bonusAmount"
                    title={t(form.value)}
                  />
                )}
                <SelectField
                  name="currencyCode"
                  required
                  id="RewardPlayerCreateForm__currencyCode"
                  title={t(form.currency)}
                  options={currencyOptions}
                />
              </div>
              {isFreeSpins && (
                <>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <NumberField
                      name="spinsNumber"
                      required
                      id="RewardPlayerCreateForm__spinsNumber"
                      title={t(form.numberOfSpins)}
                      step="1"
                    />
                    <NumberField
                      name="betLevel"
                      required
                      id="RewardPlayerCreateForm__betLevel"
                      title={t(form.paylines)}
                      step="1"
                    />
                  </div>
                  <SelectField
                    name="gameProvider"
                    id="RewardPlayerCreateForm__gameProviders"
                    title={t(form.gameProviders)}
                    options={rewardGameProviders}
                  />
                  <GameSearch
                    name="gameDetails"
                    id="RewardPlayerCreateForm__gameDetails"
                    title={t(form.game)}
                    providerName={selectedGameProvider}
                  />
                </>
              )}
              {rewardType === 'BonusMoneyDropType' && (
                <>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <TextField
                      name="maxPayout"
                      required
                      id="RewardCreateForm__maxPayout"
                      title={t(form.maxPayout)}
                    />
                    <NumberField
                      name="expiresIn.days"
                      required
                      id="RewardCreateForm__expiresIn"
                      title={t(form.expiresIn)}
                      step="1"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <NumberField
                      name="wageringRequirementFactor"
                      required
                      id="RewardCreateForm__wageringRequirementFactor"
                      title={t(form.wageringRequirementFactor)}
                      step="1"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="grid gap-2 content-start">
              <TextField
                name="rewardName"
                id="RewardPlayerCreateForm__rewardName"
                title={t(form.nameOfReward)}
                required
              />
              <div className="grid sm:grid-cols-2 gap-2">
                <DateTimeField
                  title={t(form.startDate)}
                  name="startsAt"
                  id="analytics-block-settings__startsAt"
                  required
                />
                <DateTimeField
                  title={t(form.endDate)}
                  name="endsAt"
                  id="analytics-block-settings__endsAt"
                  required
                />
              </div>
              <SelectField
                name="claimType"
                id="RewardPlayerCreateForm__claimType"
                title={t(form.claimableInstant)}
                options={claimOptions}
                required
              />
            </div>
            <div className="grid gap-2 content-start">
              <TextareaField
                name="comment"
                title={t(form.notes)}
                id="RewardPlayerCreateForm__comment"
              />
            </div>
          </div>
          <ErrorMessage message={errorMessage} />
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={resetAllButRewardType}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                createFreeSpinsState.fetching ||
                createMoneyDropState.fetching ||
                createBonusMoneyDropState.fetching
              }
            >
              Create Reward
            </Button>
          </div>
        </NakedForm>
      </div>
    </Card>
  );
};

export default PlayerRewardCreateForm;
